import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, MonitorPlay, Megaphone, Award } from "lucide-react";
import api, { API } from "../lib/api";
import { LiveBadge } from "../components/Brand";
import { useAuth } from "../context/AuthContext";

function LobbyHero({ settings, user, firstRoomId }) {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden rounded-2xl border border-[#00C8FF]/25 gradient-brand p-8 sm:p-12">
      <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #00C8FF 0%, transparent 40%)" }} />
      <div className="relative">
        <p className="text-xs font-data tracking-[0.3em] uppercase text-[#00C8FF] mb-3">Transmissão ao vivo</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-3">
          {settings?.event_name || "Pregoeiros Summit"}
        </h1>
        <p className="text-base text-slate-300 max-w-2xl">
          {settings?.event_date ? `Edição ${settings.event_date}. ` : ""}
          Bem-vindo(a), {user?.name || user?.email}! Escolha uma sala abaixo e aproveite o evento.
        </p>
        {firstRoomId && (
          <button onClick={() => navigate(`/sala/${firstRoomId}`)} className="btn-primary mt-6" data-testid="cta-main-room">
            Entrar na Plenária
          </button>
        )}
      </div>
    </section>
  );
}

function AnnouncementsBanner({ announcements }) {
  if (announcements.length === 0) return null;
  return (
    <section className="bg-[#008EFF]/15 border border-[#00C8FF]/40 rounded-xl p-5" data-testid="announcements-banner">
      <div className="flex items-center gap-2 text-[#00C8FF] font-heading font-semibold mb-3">
        <Megaphone size={18} /> Avisos da organização
      </div>
      <ul className="space-y-2">
        {announcements.slice(0, 3).map((a) => (
          <li key={a.id} className="text-sm text-cyan-100 flex items-start gap-2">
            <span className="text-[#F5A623] mt-0.5">•</span>
            <span>{a.text} <span className="text-slate-500 text-xs font-data ml-1">{new Date(a.created_at).toLocaleString("pt-BR")}</span></span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RoomCard({ room, index }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/sala/${room.id}`)}
      className="card-room text-left group"
      data-testid={index === 0 ? "room-card-plenaria" : `room-card-sala-${index}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-lg bg-[#0C1530] border border-[#00C8FF]/30 flex items-center justify-center text-[#00C8FF] group-hover:scale-110 transition-transform">
          <MonitorPlay size={22} />
        </div>
        <LiveBadge live={room.is_live} />
      </div>
      <h3 className="text-xl font-semibold text-white mb-1.5">{room.name}</h3>
      <p className="text-sm text-slate-400 line-clamp-2 min-h-[2.5rem]">{room.description || "Transmissão e chat ao vivo"}</p>
      <span className="inline-block mt-4 text-sm font-bold uppercase tracking-wider text-[#00C8FF] group-hover:text-white transition-colors">
        Entrar na sala →
      </span>
    </button>
  );
}

function RoomsGrid({ rooms }) {
  return (
    <section>
      <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-5">Salas do evento</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {rooms.map((room, i) => <RoomCard key={room.id} room={room} index={i} />)}
        {rooms.length === 0 && (
          <div className="card-room text-slate-400 text-sm">As salas serão abertas em breve pela organização.</div>
        )}
      </div>
    </section>
  );
}

function MaterialsCard({ hasMaterial, onDownload }) {
  return (
    <div className="card-room">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Download size={18} className="text-[#00C8FF]" /> Materiais do evento
      </h3>
      <div className="space-y-3">
        <button onClick={() => onDownload("apostila")} disabled={!hasMaterial("apostila")}
          className="btn-secondary w-full flex items-center justify-between" data-testid="download-handout-btn">
          <span>Apostila do evento</span>
          <span className="text-xs font-data">{hasMaterial("apostila") ? "PDF" : "em breve"}</span>
        </button>
        <button onClick={() => onDownload("programacao")} disabled={!hasMaterial("programacao")}
          className="btn-secondary w-full flex items-center justify-between" data-testid="download-schedule-btn">
          <span>Programação completa</span>
          <span className="text-xs font-data">{hasMaterial("programacao") ? "PDF" : "em breve"}</span>
        </button>
      </div>
    </div>
  );
}

function CertificateCard({ released }) {
  const navigate = useNavigate();
  return (
    <div className="card-room">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Award size={18} className="text-[#F5A623]" /> Seu certificado
      </h3>
      {released ? (
        <>
          <p className="text-sm text-slate-300 mb-4">Seu certificado de participação já está disponível.</p>
          <button onClick={() => navigate("/certificado")} className="btn-primary w-full" data-testid="lobby-certificate-btn">
            Baixar certificado
          </button>
        </>
      ) : (
        <p className="text-sm text-slate-400">
          O certificado será liberado pela organização após o evento e você receberá um aviso por e-mail.
        </p>
      )}
    </div>
  );
}

export default function Lobby() {
  const [rooms, setRooms] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [settings, setSettings] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const [r, a, m, s] = await Promise.all([
        api.get("/rooms"), api.get("/announcements"), api.get("/materials"), api.get("/settings"),
      ]);
      setRooms(r.data);
      setAnnouncements(a.data);
      setMaterials(m.data);
      setSettings(s.data);
    } catch (err) {
      console.error("Falha ao carregar lobby:", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const download = useCallback((type) => {
    window.open(`${API}/materials/${type}/download?v=${Date.now()}`, "_blank", "noopener");
  }, []);

  const hasMaterial = useCallback((type) => materials.some((m) => m.type === type), [materials]);

  return (
    <div className="space-y-10" data-testid="participant-lobby">
      <LobbyHero settings={settings} user={user} firstRoomId={rooms[0]?.id} />
      <AnnouncementsBanner announcements={announcements} />
      <RoomsGrid rooms={rooms} />
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MaterialsCard hasMaterial={hasMaterial} onDownload={download} />
        <CertificateCard released={user?.certificate_released} />
      </section>
    </div>
  );
}
